import { memo, useMemo } from 'react';

import { CardTabs, CardTabsProps } from '@hcs/design-system';
import { PlusIcon, TextButton } from '@hcs/design-system';
import { useScreenSize } from '@hcs/hooks';

import { MACRO_INSIGHTS_FROM_DATE } from '../../constants';
import { CHART_TAB_IDS } from '../../constants';
import {
  useCerberusStatsFields,
  useCityListingStatsTimeSeries,
} from '../../hooks';
import { CityListingStatsChart } from '../CityListingStatsChart';
import {
  MARKET_STATS_FOR_USER_GEO_TYPE,
  MarketStateChartsForUserLauncher,
} from '../MarketStateChartsForUser';
import { GeoTypeChartDropdownMobile } from '../ZipMsaChartDropdownMobile';

import styles from './CityCharts.module.css';
interface CityChartsProps {
  className?: string;
  placeId: string;
  isNarrowView?: boolean;
  propertyType: 'singleFamily' | 'condo';
  setPropertyType?: (propertyType: 'singleFamily' | 'condo') => void;
}

const dataHcName = 'city-charts';

export const CityCharts = memo(
  ({
    className,
    placeId,
    isNarrowView,
    propertyType,
    setPropertyType,
  }: CityChartsProps) => {
    const { isPhoneWidth } = useScreenSize();
    const { data: listingStatsTS } = useCityListingStatsTimeSeries({
      placeId,
      fromDate: MACRO_INSIGHTS_FROM_DATE,
      propertyType,
    });
    // Using chartsZip custom fields for city for now - we can make this more specific in the future if needed
    const { data: customFieldsData } = useCerberusStatsFields('chartsZip');

    const tabs: CardTabsProps['tabs'] = useMemo(() => {
      const hasMarketActivityData =
        listingStatsTS != null &&
        (listingStatsTS?.rentalListingStats.length > 0 ||
          listingStatsTS?.saleListingStats.length > 0);

      if (hasMarketActivityData) {
        const MarketActivityTab = {
          tabId: CHART_TAB_IDS.MarketActivity,
          label: <>Market Activity</>,
          content: (
            <div className={styles.ChartsTabContent}>
              {customFieldsData?.order.map((cerberusField, index) => (
                <CityListingStatsChart
                  key={cerberusField}
                  className={styles.ChartSection}
                  cerberusStatsField={cerberusField}
                  placeId={placeId}
                  propertyType={propertyType}
                  setPropertyType={setPropertyType}
                  showPropertyTypeWarning={index === 0}
                />
              ))}
            </div>
          ),
        };
        return [MarketActivityTab];
      }
      return [];
    }, [
      customFieldsData,
      placeId,
      listingStatsTS,
      propertyType,
      setPropertyType,
    ]);

    const chooseFieldsContent = (
      <MarketStateChartsForUserLauncher
        showTooltip={false}
        // Using chartsZip custom fields for city for now - we can make this more specific in the future if needed
        geoType={MARKET_STATS_FOR_USER_GEO_TYPE.ZIP}
        trigger={
          <TextButton
            dataHcName={`${dataHcName}-data-priority`}
            icon={<PlusIcon size="sm" />}
            noPadding
          >
            Choose Fields
          </TextButton>
        }
      />
    );

    return isPhoneWidth || isNarrowView ? (
      <GeoTypeChartDropdownMobile
        chartSelector={chooseFieldsContent}
        market={tabs[0]?.content}
        macro={undefined}
      />
    ) : (
      <CardTabs
        searchParamId={dataHcName}
        dataHcName={dataHcName}
        className={className}
        tabs={tabs}
        actionTextConfig={{
          tabId: CHART_TAB_IDS.MarketActivity,
          content: (
            <div className={styles.DataPriorityControlContainer}>
              {chooseFieldsContent}
            </div>
          ),
        }}
        active={CHART_TAB_IDS.MarketActivity}
      />
    );
  },
);
